import uuid from 'node-uuid';
import moment from 'moment';
import { head, get } from 'lodash';
import {
	BACS_UPDATE_FROM_ACCOUNT,
	BACS_UPDATE_TO_ACCOUNT,
	BACS_UPDATE_SUBMISSION_DATE,
	BACS_ADD_PAYMENT,
	BACS_ADD_MULTIPLE_PAYMENTS,
	BACS_REMOVE_PAYMENT,
	BACS_UPDATE_AMOUNT,
	BACS_UPDATE_REFERENCE,
	BACS_UPDATE_BACS_ID,
	BACS_UPDATE_PAYMENT_NAME,
	BACS_UPDATE_HMRC_REF,
	BACS_COPY_REF_TO_ALL,
	INITIALISE_BACS_CREDIT,
	BACS_UPDATE_PAYMENT_ID,
	BACS_CREDIT_SET_AUTHORISERS,
	BACS_UPDATE_IMPORTED_PAYMENTS,
} from './actions';
import { GO_BACK_PAGE, RESET_APP, CREATE_NEW_BACS_TEMPLATE } from '../page/actions';
import { SUBMIT_CALENDAR_SUCCESS } from '../calendar/actions';
import { defaultBacsPaymentName, TYPE_BACS_CREDIT, CUSTOMER_DEVICE_ENROLLED_ERROR_CODE } from '../../config';
import { BENEFICIARY_CREATE_SUCCESS } from '../payees/actions';
import {
	SUBMIT_BACS_PAYMENT_REVIEW_SUCCESS,
	SUBMIT_CONFIRMED_BACS_PAYMENTS_SUCCESS,
	SUBMIT_CONFIRMED_BACS_PAYMENTS_FAILURE,
	SUBMIT_BACS_PAYMENT_REVIEW_FAILURE,
} from '../api/bacsActions';
import { CANCEL_PAYMENT_APPROVAL_SUCCESS } from '../api/fasterPaymentActions';
import { isPaymentTRAObligate } from '../../utils/authoriserUtils';
import { isStateDateInvalid } from '../commonSelectors/selectors';
import { getAuthorisationInfo } from '../../helpers/reducerHelper';

const defaultPaymentState = ({ reference, ...paymentExcludingReference } = {}) => ({
	toAccount: '',
	amount: '',
	reference,
	initialReference: reference,
	hmrcReference: '',
	approverId: undefined,
	...paymentExcludingReference,
});

const paymentHandlers = {
	[BACS_UPDATE_TO_ACCOUNT]: (prevPayment, action) => ({ ...prevPayment, toAccount: action.accountId }),
	[BACS_UPDATE_REFERENCE]: (prevPayment, action) => ({ ...prevPayment, reference: action.reference }),
	[BACS_UPDATE_AMOUNT]: (prevPayment, action) => ({ ...prevPayment, amount: action.amount }),
	[BACS_UPDATE_HMRC_REF]: (prevPayment, action) => ({ ...prevPayment, hmrcReference: action.hmrcReference }),
};

const getPayment = (prevPayment, action) => (paymentHandlers[action.type] || ((s) => s))(prevPayment, action);

const setPayment = ({ payments = [] }, payment = {}) =>
	payments.map((item) => {
		if (item.id !== payment.id) {
			return item;
		}
		return {
			...item,
			...payment,
		};
	});

const removePayment = ({ payments = [] }, { id = '' }) => {
	const newPayments = payments.slice();
	const index = newPayments.findIndex((payment) => payment.id === id);
	newPayments.splice(index, 1);
	return newPayments;
};

/**
 * Every time initialState is called, we get a new UUID v1 value as `id`
 */
const initialState = () => ({
	payments: [],
	fromAccount: '',
	submissionDate: '',
	id: uuid.v1(),
	paymentName: defaultBacsPaymentName,
	importedPayments: [],
});

const bacsCredit = (state = initialState(), action) => {
	if (Object.keys(paymentHandlers).includes(action.type)) {
		const payment = getPayment(
			state.payments.find((payment) => action.id === payment.id),
			action
		);
		if (action.type === BACS_UPDATE_AMOUNT) {
			return { ...state, payments: setPayment(state, payment), maxLimitExceeded: false };
		}
		return { ...state, payments: setPayment(state, payment) };
	}
	if (action.type === INITIALISE_BACS_CREDIT) {
		return { ...state, ...action.bacsDetails, payments: action.payments };
	}

	if (action.type === BACS_UPDATE_PAYMENT_NAME) {
		return { ...state, paymentName: action.paymentName };
	}
	if (action.type === BACS_UPDATE_FROM_ACCOUNT) {
		const payments = state.payments.map((payment) => {
			if (payment.toAccount === action.accountId) {
				return {
					id: payment.id,
					...defaultPaymentState(),
				};
			}
			return payment;
		});
		return { ...state, fromAccount: action.accountId, bacsId: '', payments };
	}

	if (action.type === BACS_UPDATE_SUBMISSION_DATE) {
		return { ...state, submissionDate: action.submissionDate, settlementDate: action.settlementDate };
	}

	if (action.type === BACS_ADD_PAYMENT) {
		const payments = [...state.payments, defaultPaymentState(action.payment)];
		return { ...state, payments };
	}

	if (action.type === CREATE_NEW_BACS_TEMPLATE) {
		if (action.bacsType === TYPE_BACS_CREDIT) {
			const payments = [...state.payments, defaultPaymentState({ id: action.id })];
			return { ...state, payments };
		}
		return state;
	}

	if (action.type === BACS_ADD_MULTIPLE_PAYMENTS) {
		const paymentsToAdd = action.payments.map((payment) => defaultPaymentState(payment));
		const payments = [...state.payments, ...paymentsToAdd];
		return { ...state, payments };
	}

	if (action.type === BACS_REMOVE_PAYMENT) {
		const payments = removePayment(state, action);
		return { ...state, payments, maxLimitExceeded: false };
	}

	if (action.type === BACS_UPDATE_BACS_ID) {
		return { ...state, bacsId: action.bacsId };
	}

	if (action.type === BACS_UPDATE_PAYMENT_NAME) {
		return { ...state, paymentName: action.paymentName };
	}

	if (action.type === SUBMIT_BACS_PAYMENT_REVIEW_SUCCESS) {
		const userId = get(action, 'meta.userId');
		const {
			authorisation,
			singleAuthoriserList,
			multiAuthoriserList,
			authorisers,
			authorisersGroup,
			isDefaultAuthorisers,
		} = getAuthorisationInfo(userId, action.payload);

		const isTRAObligate = isPaymentTRAObligate(
			authorisation.ben_id_present,
			singleAuthoriserList,
			authorisation.status,
			userId
		);
		const { paymentName = '' } = state;
		return {
			...state,
			authorisation,
			singleAuthoriserList,
			multiAuthoriserList,
			authorisers,
			authorisersGroup,
			paymentName: paymentName.trim() === '' ? defaultBacsPaymentName : paymentName,
			isDefaultAuthorisers,
			isTRAObligate,
		};
	}

	if (action.type === SUBMIT_CONFIRMED_BACS_PAYMENTS_SUCCESS) {
		const confirmation = { ...action.payload };
		return { ...state, confirmation };
	}

	if (action.type === SUBMIT_CONFIRMED_BACS_PAYMENTS_FAILURE) {
		if (action.payload.status === 409) {
			if (get(action, 'payload.response.error.code', '') === '1010') return { ...state, deadlineMissed: true };
		}
		if (action.payload.status === 401) {
			const confirmation = { ...action.payload.response, responseCode: 401 };
			return { ...state, confirmation };
		}
		if (get(action, 'payload.response.error.code', 0) === CUSTOMER_DEVICE_ENROLLED_ERROR_CODE) {
			const confirmation = { error: { ...action.payload.response.error, responseCode: action.payload.status } };
			return { ...state, confirmation };
		}
		return state;
	}

	if (action.type === SUBMIT_CALENDAR_SUCCESS) {
		const firstDate = head(action.payload.dates);
		const isSubmissionDateInvalid = isStateDateInvalid(state.submissionDate, firstDate.submission);
		const isSettlementDateInvalid = isStateDateInvalid(state.settlementDate, firstDate.settlement);

		if (isSubmissionDateInvalid || isSettlementDateInvalid) {
			const submissionDate = firstDate.submission ? moment(firstDate.submission) : '';
			const settlementDate = firstDate.settlement ? moment(firstDate.settlement) : '';
			return { ...state, submissionDate, settlementDate };
		}
		return state;
	}

	if (action.type === BACS_COPY_REF_TO_ALL) {
		const { reference } = head(state.payments);
		const paymentsWithSameRef = state.payments.map((payment) => {
			if (action.paymentsToInternalCreditCards.includes(payment.id)) return payment;
			return { ...payment, reference };
		});
		return { ...state, payments: paymentsWithSameRef };
	}

	if (action.type === SUBMIT_BACS_PAYMENT_REVIEW_FAILURE) {
		if (action.payload.status === 409) {
			return {
				...state,
				maxLimitExceeded: true,
			};
		}

		return state;
	}
	if (action.type === BENEFICIARY_CREATE_SUCCESS) {
		if (action.payload.apiResponses && action.payload.apiResponses.filter((r) => r.code !== 201)) {
			const updateBeneficiaryForInternalAccounts = state.payments.map((payment) => {
				const targetBenID = get(
					head(
						action.payload.apiResponses
							.filter((r) => r.code === 201)
							.filter((resp) => get(resp, 'body.account_id') === payment.toAccount)
					),
					'body.id'
				);

				if (targetBenID) {
					return { ...payment, beneficiary_id: targetBenID };
				}

				return payment;
			});
			return { ...state, payments: updateBeneficiaryForInternalAccounts };
		}
		return state;
	}

	if (action.type === GO_BACK_PAGE) {
		return { ...state, deadlineMissed: false };
	}

	if (action.type === RESET_APP) {
		const { firstDate } = action;
		if (firstDate) {
			const submissionDate = firstDate.submission ? moment(firstDate.submission) : '';
			const settlementDate = firstDate.settlement ? moment(firstDate.settlement) : '';
			return { ...initialState(), submissionDate, settlementDate };
		}
		return initialState();
	}
	if (action.type === BACS_UPDATE_PAYMENT_ID) {
		return {
			...state,
			...action.bacsDetails,
		};
	}
	if (action.type === CANCEL_PAYMENT_APPROVAL_SUCCESS) {
		return { ...state, payments: state.payments.map((payment) => ({ ...payment, id: uuid.v1() })) };
	}
	if (action.type === BACS_CREDIT_SET_AUTHORISERS) {
		return { ...state, authorisers: action.authorisers };
	}
	if (action.type === BACS_UPDATE_IMPORTED_PAYMENTS) {
		return { ...state, importedPayments: action.payload };
	}

	return state;
};

export default bacsCredit;
